/**
 * Shared pricing query identity for client band / suggest caches.
 *
 * Stable attr serialization so key order / null-omit cannot split caches.
 * Engine tier caches still use `pricingQueryAttrCacheSegment` (D15).
 */

/** Bump when sell-band client identity shape changes. */
export const SELL_PRICE_BAND_CACHE_GEN = "qattrs1";

/**
 * Bump when admin seller-band client identity shape changes
 * (was `qattrs2` before shared identity).
 */
export const ADMIN_SELLER_BAND_CACHE_GEN = "qattrs3";

function stableJsonValue(value: unknown): unknown {
  if (value === null || value === undefined) {
    return null;
  }
  if (Array.isArray(value)) {
    return value.map(stableJsonValue);
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(record).sort()) {
      const next = record[key];
      if (next === undefined) {
        continue;
      }
      sorted[key] = stableJsonValue(next);
    }
    return sorted;
  }
  return value;
}

/**
 * Deterministic JSON for listing attrs (sorted object keys).
 * Empty / null / undefined → `""` (no cache segment).
 */
export function stableSerializeListingAttributes(
  listingAttributes?: Record<string, unknown> | null,
): string {
  if (!listingAttributes || Object.keys(listingAttributes).length === 0) {
    return "";
  }
  return JSON.stringify(stableJsonValue(listingAttributes));
}

/** Cache suffix `_a_{...}` or empty when no attrs. */
export function listingAttributesCacheSegment(
  listingAttributes?: Record<string, unknown> | null,
): string {
  const serialized = stableSerializeListingAttributes(listingAttributes);
  return serialized ? `_a_${serialized}` : "";
}

export function excludeItemIdCacheSegment(excludeItemId?: string): string {
  return excludeItemId ? `_ex_${excludeItemId}` : "";
}

export type PricingBandCacheIdentity = {
  title: string;
  category: string;
  subcategory: string;
  listingAttributes?: Record<string, unknown> | null;
  excludeItemId?: string;
};

/** Sell-flow session band cache key. */
export function buildSellPriceBandCacheKey(
  params: PricingBandCacheIdentity,
): string {
  const title = params.title.trim().toLowerCase();
  const category = params.category.trim();
  return `${category}|${params.subcategory}|${title}|${SELL_PRICE_BAND_CACHE_GEN}${excludeItemIdCacheSegment(params.excludeItemId)}${listingAttributesCacheSegment(params.listingAttributes)}`;
}

/** Admin seller-band session cache key (includes data source). */
export function buildAdminSellerBandCacheKey(
  params: PricingBandCacheIdentity & { dataSource: string },
): string {
  const title = params.title.trim().toLowerCase();
  const category = params.category.trim();
  return `${params.dataSource}|${category}|${params.subcategory}|${title}|${ADMIN_SELLER_BAND_CACHE_GEN}${excludeItemIdCacheSegment(params.excludeItemId)}${listingAttributesCacheSegment(params.listingAttributes)}`;
}
